import * as i7 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Input, EventEmitter, Optional, Output, Inject, Injectable, NgModule } from '@angular/core';
import * as i5$1 from '@angular/forms';
import { NgControl, NG_VALIDATORS, ControlContainer, NgForm, FormsModule } from '@angular/forms';
import * as i1 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i1$1 from '@angular/material/dialog';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import * as i2 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i4 from '@angular/material/input';
import { MatInputModule } from '@angular/material/input';
import * as i3 from '@angular/material/tooltip';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as i2$1 from '@firestitch/dialog';
import { FsDialogModule } from '@firestitch/dialog';
import * as i6 from '@firestitch/form';
import { FsFormModule } from '@firestitch/form';
import { FsMessageModule } from '@firestitch/message';
import { controlContainerFactory } from '@firestitch/core';
import * as i5 from '@angular/material/form-field';
import { Subject, merge, fromEvent, Observable } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { PasswordMeter } from 'password-meter';

class FsPasswordComponent {
    constructor(_el, _injector, _cdRef) {
        this._el = _el;
        this._injector = _injector;
        this._cdRef = _cdRef;
        this.visible = false;
        this.strength = false;
        this.acceptable = false;
        this.passwordHint = '';
        this._destroy$ = new Subject();
    }
    get element() {
        return this._el.nativeElement;
    }
    toggle(e) {
        e.preventDefault();
        e.stopPropagation();
        this.visibleToggle = !this.visibleToggle;
        this.updateType();
    }
    updateType() {
        this._el.nativeElement.setAttribute('type', this.visibleToggle ? 'text' : 'password');
    }
    ngOnInit() {
        this._ngControl = this._injector.get(NgControl);
        if (this.strength) {
            this.strengthConfig = {
                minLength: 8,
                ...this.strengthConfig,
            };
            this.passwordMeter = new PasswordMeter(this.strengthConfig);
        }
        this.visibleToggle = this.visible;
        this.updateType();
        // Used to fix iOS chrome autofill issue
        // https://github.com/angular/components/issues/3414
        merge(fromEvent(this.element, 'change'))
            .pipe(take(1), takeUntil(this._destroy$))
            .subscribe((event) => {
            this._ngControl.control.setValue(event.target.value);
        });
    }
    validate(control) {
        if (!this.strength) {
            return null;
        }
        const value = control.value || '';
        const result = this.passwordMeter.getResult(value);
        this.acceptable = result.percent >= 60;
        let prefix = '';
        this.passwordMeterLevel = null;
        if (control.dirty) {
            if (this.acceptable) {
                this.passwordMeterLevel = 'strong';
                this.passwordHint = 'Your password is strong';
                if (result.percent >= 90) {
                    this.passwordHint = 'Amazing! Your password is very strong';
                }
            }
            else if (result.percent >= 40) {
                prefix = 'Could be stronger';
                this.passwordMeterLevel = 'medium';
            }
            else {
                prefix = 'Too weak';
                this.passwordMeterLevel = 'weak';
            }
        }
        this._updateFormField();
        if (this.acceptable) {
            return null;
        }
        if (value.length === 0) {
            this.passwordHint = this.defaultPasswordHint;
        }
        else if (!value.match(/\W/)) {
            this.passwordHint = `${prefix}, try including a special character`;
        }
        else if (!value.match(/[A-Z]/)) {
            this.passwordHint = `${prefix}, try including an uppercase character`;
        }
        else if (value.length < this.strengthConfig.minLength) {
            this.passwordHint = this.defaultPasswordHint;
        }
        else {
            this.passwordHint = `${prefix}, try adding another word or two`;
        }
        return { passwordStrength: this.passwordHint };
    }
    get matFormField() {
        return this._el.nativeElement.parentElement.parentElement.parentElement.parentElement;
    }
    ngAfterViewInit() {
        if (this.strength) {
            const matFormField = this.matFormField;
            const matFormFieldFlex = matFormField.querySelector('.mat-form-field-flex');
            const matUnderline = matFormFieldFlex.parentElement.querySelector('.mat-form-field-underline');
            if (matUnderline) {
                matUnderline
                    .after(this._el.nativeElement.querySelector('.fs-password-meter'));
            }
            const matHintWrapper = matFormFieldFlex.parentElement.querySelector('.mat-form-field-hint-wrapper');
            matHintWrapper.prepend(this._el.nativeElement.querySelector('.fs-password-hint'));
        }
    }
    get defaultPasswordHint() {
        return `Make sure it's ${this.strengthConfig.minLength} characters or more`;
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    _updateFormField() {
        this.matFormField.classList.remove('password-weak');
        this.matFormField.classList.remove('password-medium');
        this.matFormField.classList.remove('password-strong');
        if (this.passwordMeterLevel === 'weak') {
            this.matFormField.classList.add('password-weak');
        }
        if (this.passwordMeterLevel === 'medium') {
            this.matFormField.classList.add('password-medium');
        }
        if (this.passwordMeterLevel === 'strong') {
            this.matFormField.classList.add('password-strong');
        }
    }
}
FsPasswordComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordComponent, deps: [{ token: i0.ElementRef }, { token: i0.Injector }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsPasswordComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsPasswordComponent, selector: "[fsPassword]", inputs: { visible: "visible", strength: "strength", strengthConfig: "strengthConfig" }, providers: [{
            provide: NG_VALIDATORS,
            useExisting: FsPasswordComponent,
            multi: true,
        }], ngImport: i0, template: "<span class=\"fs-password-toggle\">\n  <button type=\"button\"\n      tabindex=\"-1\"\n      [matTooltip]=\"visibleToggle ? 'Hide' : 'Show'\"\n      mat-icon-button (click)=\"toggle($event)\">\n    <mat-icon>{{ visibleToggle ? 'visibility' : 'visibility_off' }}</mat-icon>\n  </button>\n</span>\n\n<div  \n    class=\"fs-password-meter\" \n    [ngClass]=\"passwordMeterLevel\">\n  <div class=\"level level-1\"></div>\n  <div class=\"level level-2\"></div>\n  <div class=\"level level-3\"></div>\n</div>  \n\n<mat-hint class=\"fs-password-hint\">\n  {{passwordHint}}  \n</mat-hint>\n", styles: [".fs-password-toggle .meter-status{font-size:80%}.fs-password-toggle .meter-status.weak{color:#ff3548}.fs-password-toggle .meter-status.medium{color:#ffbb34}.fs-password-toggle .meter-status.strong{color:#12a34b}.fs-password-toggle .mat-icon-button{width:28px;height:28px;line-height:32px}.fs-password-toggle mat-icon{margin-bottom:5px}:host ::ng-deep .mat-form-field-wrapper{position:relative}.fs-password-meter{display:flex;position:absolute;width:100%;margin-top:-1px}.fs-password-meter .level{width:33.3333%;height:3px;background-color:#ccc}.fs-password-meter.weak .level-1{background-color:#ff3548}.fs-password-meter.medium .level-1,.fs-password-meter.medium .level-2{background-color:#ffbb34}.fs-password-meter.strong .level-1,.fs-password-meter.strong .level-2,.fs-password-meter.strong .level-3{background-color:#12a34b}::ng-deep .mat-form-field-appearance-outline.password-weak .mat-form-field-outline,::ng-deep .mat-form-field-appearance-outline.password-weak .fs-password-hint{color:#ff3548!important}::ng-deep .mat-form-field-appearance-outline.password-medium .mat-form-field-outline,::ng-deep .mat-form-field-appearance-outline.password-medium .fs-password-hint{color:#ffbb34!important}::ng-deep .mat-form-field-appearance-outline.password-strong .mat-form-field-outline,::ng-deep .mat-form-field-appearance-outline.password-strong .fs-password-hint{color:#12a34b!important}\n"], components: [{ type: i1.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }, { type: i2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }], directives: [{ type: i3.MatTooltip, selector: "[matTooltip]", exportAs: ["matTooltip"] }, { type: i7.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i5.MatHint, selector: "mat-hint", inputs: ["align", "id"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordComponent, decorators: [{
            type: Component,
            args: [{ selector: '[fsPassword]', changeDetection: ChangeDetectionStrategy.OnPush, providers: [{
                            provide: NG_VALIDATORS,
                            useExisting: FsPasswordComponent,
                            multi: true,
                        }], template: "<span class=\"fs-password-toggle\">\n  <button type=\"button\"\n      tabindex=\"-1\"\n      [matTooltip]=\"visibleToggle ? 'Hide' : 'Show'\"\n      mat-icon-button (click)=\"toggle($event)\">\n    <mat-icon>{{ visibleToggle ? 'visibility' : 'visibility_off' }}</mat-icon>\n  </button>\n</span>\n\n<div  \n    class=\"fs-password-meter\" \n    [ngClass]=\"passwordMeterLevel\">\n  <div class=\"level level-1\"></div>\n  <div class=\"level level-2\"></div>\n  <div class=\"level level-3\"></div>\n</div>  \n\n<mat-hint class=\"fs-password-hint\">\n  {{passwordHint}}  \n</mat-hint>\n", styles: [".fs-password-toggle .meter-status{font-size:80%}.fs-password-toggle .meter-status.weak{color:#ff3548}.fs-password-toggle .meter-status.medium{color:#ffbb34}.fs-password-toggle .meter-status.strong{color:#12a34b}.fs-password-toggle .mat-icon-button{width:28px;height:28px;line-height:32px}.fs-password-toggle mat-icon{margin-bottom:5px}:host ::ng-deep .mat-form-field-wrapper{position:relative}.fs-password-meter{display:flex;position:absolute;width:100%;margin-top:-1px}.fs-password-meter .level{width:33.3333%;height:3px;background-color:#ccc}.fs-password-meter.weak .level-1{background-color:#ff3548}.fs-password-meter.medium .level-1,.fs-password-meter.medium .level-2{background-color:#ffbb34}.fs-password-meter.strong .level-1,.fs-password-meter.strong .level-2,.fs-password-meter.strong .level-3{background-color:#12a34b}::ng-deep .mat-form-field-appearance-outline.password-weak .mat-form-field-outline,::ng-deep .mat-form-field-appearance-outline.password-weak .fs-password-hint{color:#ff3548!important}::ng-deep .mat-form-field-appearance-outline.password-medium .mat-form-field-outline,::ng-deep .mat-form-field-appearance-outline.password-medium .fs-password-hint{color:#ffbb34!important}::ng-deep .mat-form-field-appearance-outline.password-strong .mat-form-field-outline,::ng-deep .mat-form-field-appearance-outline.password-strong .fs-password-hint{color:#12a34b!important}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Injector }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { visible: [{
                type: Input
            }], strength: [{
                type: Input
            }], strengthConfig: [{
                type: Input
            }] } });

class FsPasswordChangeComponent {
    constructor() {
        this.config = null;
        this.currentPasswordChange = new EventEmitter();
        this.newPasswordChange = new EventEmitter();
        this.excludeFormFunction = ((formControl) => {
            this.config.exclude.forEach((word) => {
                if (this.newPasswordValue && this.newPasswordValue.toLowerCase().indexOf(word.toLowerCase()) !== -1) {
                    throw new Error(`The password cannot be the word '${this.newPasswordValue}'`);
                }
            });
        });
    }
    get currentPassword() {
        return this.currentPasswordValue;
    }
    set currentPassword(value) {
        this.currentPasswordValue = value;
        this.currentPasswordChange.emit(this.currentPasswordValue);
    }
    get newPassword() {
        return this.newPasswordValue;
    }
    set newPassword(value) {
        this.newPasswordValue = value;
        this.newPasswordChange.emit(this.newPasswordValue);
    }
    ngOnInit() {
        this._setDefaultConfig();
    }
    _setDefaultConfig() {
        this.config = { minLength: 6,
            enableCurrentPassword: true,
            exclude: [], ...this.config };
    }
}
FsPasswordChangeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordChangeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsPasswordChangeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsPasswordChangeComponent, selector: "fs-password", inputs: { config: "config", currentPassword: "currentPassword", newPassword: "newPassword" }, outputs: { currentPasswordChange: "currentPasswordChange", newPasswordChange: "newPasswordChange" }, ngImport: i0, template: "<div class=\"fs-column\" ngFormGroup>\n  <mat-form-field *ngIf=\"config.enableCurrentPassword\">\n    <input\n      matInput\n      fsPassword\n      placeholder=\"Current Password\"\n      autocomplete=\"false\"\n      name=\"passwordCurrent\"\n      [autocomplete]=\"'off'\"\n      [(ngModel)]=\"currentPassword\"\n      [fsFormRequired]=\"true\">\n  </mat-form-field>\n\n  <mat-form-field>\n    <input\n      matInput\n      fsPassword\n      [strength]=\"config.strength\"\n      [strengthConfig]=\"config.strengthConfig\"\n      placeholder=\"Create Password\"\n      autocomplete=\"false\"\n      name=\"passwordNew\"\n      [autocomplete]=\"'off'\"\n      [(ngModel)]=\"newPassword\"\n      [fsFormRequired]=\"true\"\n      [fsFormMinLength]=\"config.minLength\"\n      [fsFormFunction]=\"excludeFormFunction\">\n  </mat-form-field>\n</div>\n", styles: ["mat-icon{cursor:pointer}\n"], components: [{ type: i5.MatFormField, selector: "mat-form-field", inputs: ["color", "appearance", "hideRequiredMarker", "hintLabel", "floatLabel"], exportAs: ["matFormField"] }, { type: FsPasswordComponent, selector: "[fsPassword]", inputs: ["visible", "strength", "strengthConfig"] }], directives: [{ type: i7.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i4.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { type: i5$1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i5$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i5$1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { type: i6.FsFormRequiredDirective, selector: "[fsFormRequired],[ngModel][required]", inputs: ["fsFormRequired", "required", "fsFormRequiredMessage"] }, { type: i6.FsFormMinLengthDirective, selector: "[fsFormMinLength]", inputs: ["fsFormMinLength", "fsFormMinLengthMessage"] }, { type: i6.FsFormFunctionDirective, selector: "[fsFormFunction]", inputs: ["fsFormFunction", "fsFormFunctionData", "validateOnSubmit"] }], viewProviders: [
        {
            provide: ControlContainer,
            useFactory: controlContainerFactory,
            deps: [[new Optional(), NgForm]],
        },
    ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordChangeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-password', viewProviders: [
                        {
                            provide: ControlContainer,
                            useFactory: controlContainerFactory,
                            deps: [[new Optional(), NgForm]],
                        },
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"fs-column\" ngFormGroup>\n  <mat-form-field *ngIf=\"config.enableCurrentPassword\">\n    <input\n      matInput\n      fsPassword\n      placeholder=\"Current Password\"\n      autocomplete=\"false\"\n      name=\"passwordCurrent\"\n      [autocomplete]=\"'off'\"\n      [(ngModel)]=\"currentPassword\"\n      [fsFormRequired]=\"true\">\n  </mat-form-field>\n\n  <mat-form-field>\n    <input\n      matInput\n      fsPassword\n      [strength]=\"config.strength\"\n      [strengthConfig]=\"config.strengthConfig\"\n      placeholder=\"Create Password\"\n      autocomplete=\"false\"\n      name=\"passwordNew\"\n      [autocomplete]=\"'off'\"\n      [(ngModel)]=\"newPassword\"\n      [fsFormRequired]=\"true\"\n      [fsFormMinLength]=\"config.minLength\"\n      [fsFormFunction]=\"excludeFormFunction\">\n  </mat-form-field>\n</div>\n", styles: ["mat-icon{cursor:pointer}\n"] }]
        }], propDecorators: { config: [{
                type: Input
            }], currentPassword: [{
                type: Input
            }], currentPasswordChange: [{
                type: Output
            }], newPassword: [{
                type: Input
            }], newPasswordChange: [{
                type: Output
            }] } });

class FsPasswordDialogComponent {
    constructor(dialogRef, data) {
        this.dialogRef = dialogRef;
        this.data = data;
        this._destroy$ = new Subject();
        this.submit = () => {
            return this.data.submit(this.data.newPassword, this.data.currentPassword)
                .pipe(tap((result) => {
                this.closeDialog({ action: 'submit', result });
            }), takeUntil(this._destroy$));
        };
    }
    ngOnInit() {
        this.config = {
            minLength: this.data.minLength,
            enableCurrentPassword: this.data.enableCurrentPassword,
            exclude: this.data.exclude,
            strength: this.data.strength,
            strengthConfig: this.data.strengthConfig,
        };
        this.configStylesForButtons();
    }
    click(btn) {
        if (typeof btn.action === 'function') {
            btn.action.call();
        }
        else if (btn.action === 'cancel') {
            this.cancel();
        }
    }
    cancel() {
        this.closeDialog({ action: 'cancel' });
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    closeDialog(result) {
        this.dialogRef.close(result);
    }
    /**
     * Config obj of classes for additing it to ngClass
     */
    configStylesForButtons() {
        this.data.buttons.forEach((btn) => {
            if (btn.classList && btn.classList.length) {
                btn.classes = {};
                btn.classList.forEach((cl) => {
                    btn.classes[cl] = true;
                });
            }
        });
    }
}
FsPasswordDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordDialogComponent, deps: [{ token: i1$1.MatDialogRef }, { token: MAT_DIALOG_DATA }], target: i0.ɵɵFactoryTarget.Component });
FsPasswordDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsPasswordDialogComponent, selector: "fs-password-dialog", ngImport: i0, template: "<form fsForm [submit]=\"submit\">\n  <fs-dialog>\n    <div mat-dialog-title>{{data.title}}</div>\n    <mat-dialog-content>\n      <fs-password\n        [(currentPassword)]=\"data.currentPassword\"\n        [(newPassword)]=\"data.newPassword\"\n        [config]=\"config\">\n      </fs-password>\n    </mat-dialog-content>\n    <mat-dialog-actions>\n      <div class=\"btn-action-container\">\n        <ng-container *ngFor=\"let btn of data.buttons\">\n          <button\n              mat-button\n              [type]=\"btn.type\"\n              [ngClass]=\"btn.classes\"\n              [color]=\"btn.color\"\n              (click)=\"click(btn)\">\n            {{btn.label}}\n          </button>\n        </ng-container>\n      </div>\n    </mat-dialog-actions>\n  </fs-dialog>\n</form>\n", styles: [".btn-action-container button{margin-right:10px}.btn-action-container button:last-child{margin-right:0}\n"], components: [{ type: i2$1.FsDialogComponent, selector: "fs-dialog", inputs: ["mobileMode", "mobileActionPlacement", "mobileWidth", "mode"] }, { type: FsPasswordChangeComponent, selector: "fs-password", inputs: ["config", "currentPassword", "newPassword"], outputs: ["currentPasswordChange", "newPasswordChange"] }, { type: i1.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }], directives: [{ type: i5$1.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { type: i5$1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { type: i5$1.NgForm, selector: "form:not([ngNoForm]):not([formGroup]),ng-form,[ngForm]", inputs: ["ngFormOptions"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { type: i6.FsFormDirective, selector: "[fsForm]", inputs: ["wrapperSelector", "messageSelector", "hintSelector", "labelSelector", "autocomplete", "shortcuts", "confirm", "confirmDialog", "confirmDrawer", "confirmBrowser", "confirmTabs", "dirtySubmitButton", "submit", "successDelay", "errorDelay", "tabGroup", "deactivationGuard"], outputs: ["fsForm", "invalid", "valid", "submitted", "reseted", "cleared"], exportAs: ["fsForm"] }, { type: i1$1.MatDialogTitle, selector: "[mat-dialog-title], [matDialogTitle]", inputs: ["id"], exportAs: ["matDialogTitle"] }, { type: i1$1.MatDialogContent, selector: "[mat-dialog-content], mat-dialog-content, [matDialogContent]" }, { type: i1$1.MatDialogActions, selector: "[mat-dialog-actions], mat-dialog-actions, [matDialogActions]" }, { type: i7.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i6.FsButtonDirective, selector: "[mat-raised-button],[mat-button],[mat-flat-button],[mat-stroked-button]", inputs: ["name", "dirtySubmit", "form"] }, { type: i7.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-password-dialog', changeDetection: ChangeDetectionStrategy.OnPush, template: "<form fsForm [submit]=\"submit\">\n  <fs-dialog>\n    <div mat-dialog-title>{{data.title}}</div>\n    <mat-dialog-content>\n      <fs-password\n        [(currentPassword)]=\"data.currentPassword\"\n        [(newPassword)]=\"data.newPassword\"\n        [config]=\"config\">\n      </fs-password>\n    </mat-dialog-content>\n    <mat-dialog-actions>\n      <div class=\"btn-action-container\">\n        <ng-container *ngFor=\"let btn of data.buttons\">\n          <button\n              mat-button\n              [type]=\"btn.type\"\n              [ngClass]=\"btn.classes\"\n              [color]=\"btn.color\"\n              (click)=\"click(btn)\">\n            {{btn.label}}\n          </button>\n        </ng-container>\n      </div>\n    </mat-dialog-actions>\n  </fs-dialog>\n</form>\n", styles: [".btn-action-container button{margin-right:10px}.btn-action-container button:last-child{margin-right:0}\n"] }]
        }], ctorParameters: function () { return [{ type: i1$1.MatDialogRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DIALOG_DATA]
                }] }]; } });

class FsPasswordService {
    constructor(_dialog) {
        this._dialog = _dialog;
        this._destroy$ = new Subject();
        this._defaultDialogConfig = {
            autoFocus: false,
            disableClose: false,
            hasBackdrop: true
        };
        this._defaultButtons = [
            {
                label: 'Update password',
                action: 'submit',
                color: 'primary'
            },
            {
                label: 'Cancel',
                action: 'cancel',
                color: 'primary'
            }
        ];
    }
    open(configs) {
        return new Observable((observer) => {
            const config = this.composeConfig(configs);
            this._dialog.open(FsPasswordDialogComponent, config)
                .afterClosed()
                .pipe(takeUntil(this._destroy$))
                .subscribe(res => {
                if (res && res.action === 'submit') {
                    observer.next(res);
                }
                else {
                    observer.error({ action: 'cancel' });
                }
            });
        });
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    /**
     * Generate config with default settings and default buttons
     * @param config
     * @returns MatDialogConfig
     */
    composeConfig(config) {
        if (!config.buttons.length) {
            config.buttons = this._defaultButtons;
        }
        config.buttons.forEach((btn) => {
            btn.type = btn.action == 'submit' ? 'submit' : 'button';
        });
        if (!config.title) {
            config.title = 'Change Password';
        }
        return {
            ...this._defaultDialogConfig,
            width: config.width || this._defaultDialogConfig.width,
            data: config,
        };
    }
}
FsPasswordService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordService, deps: [{ token: i1$1.MatDialog }], target: i0.ɵɵFactoryTarget.Injectable });
FsPasswordService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.MatDialog }]; } });

class FsPasswordModule {
    static forRoot() {
        return {
            ngModule: FsPasswordModule,
            providers: [FsPasswordService],
        };
    }
}
FsPasswordModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsPasswordModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordModule, declarations: [FsPasswordChangeComponent,
        FsPasswordDialogComponent,
        FsPasswordComponent], imports: [CommonModule,
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatInputModule,
        MatTooltipModule,
        FsFormModule,
        FsMessageModule,
        FsDialogModule], exports: [FsPasswordChangeComponent,
        FsPasswordDialogComponent,
        FsPasswordComponent] });
FsPasswordModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordModule, imports: [[
            CommonModule,
            FormsModule,
            MatButtonModule,
            MatDialogModule,
            MatIconModule,
            MatInputModule,
            MatTooltipModule,
            FsFormModule,
            FsMessageModule,
            FsDialogModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsPasswordModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        MatButtonModule,
                        MatDialogModule,
                        MatIconModule,
                        MatInputModule,
                        MatTooltipModule,
                        FsFormModule,
                        FsMessageModule,
                        FsDialogModule,
                    ],
                    exports: [
                        FsPasswordChangeComponent,
                        FsPasswordDialogComponent,
                        FsPasswordComponent,
                    ],
                    declarations: [
                        FsPasswordChangeComponent,
                        FsPasswordDialogComponent,
                        FsPasswordComponent,
                    ],
                }]
        }] });

// Modules

/**
 * Generated bundle index. Do not edit.
 */

export { FsPasswordChangeComponent, FsPasswordComponent, FsPasswordDialogComponent, FsPasswordModule, FsPasswordService };
